import * as React from 'react';
import { RCS_EDITOR_DEFAULT_PERCENTAGE, RCS_TEMPLATE_EDITOR_THEME_STORAGE_ID, rcsEditorThemes } from '../constants';
import { defaultRcsConfig, defaultRcsPreviewCompilationContext, DEFAULT_SNAPSHOT_JSON } from '../defaults';
import { RcsCompilationContextForPreview } from '../utils/getCompilationContextFromSnapshotJson';

const ALLOWED_THEME_IDS = new Set(rcsEditorThemes.map(t => t.id));
const DEFAULT_THEME = 'dark';

export enum RcsTemplateEditorTabs {
  templateForm = 'templateForm',
  snapshots = 'snapshots',
  versionHistory = 'versionHistory',
}

export type ActionTypes =
  | 'SET_ACTIVE_TAB'
  | 'SET_EDITOR_SETTINGS'
  | 'SET_RIGHT_WINDOW_PERCENTAGE'
  | 'SET_UNSAVED_CHANGES'
  | 'SET_SELECTED_LANGUAGE'
  | 'SET_TEMPLATE_METADATA'
  | 'SET_RCS_CONFIG'
  | 'SET_RCS_VALIDATION_ERRORS'
  | 'DISCARD_CHANGES'
  | 'SET_LAST_SAVED_RCS_CONFIG'
  | 'UPDATE_PREVIEW_INITIAL'
  | 'SET_PREVIEW_REQUIRED'
  | 'SET_SNAPSHOT_JSON'
  | 'SET_SKIP_RANDOMISE_SNAPSHOT_MODAL'
  | 'SET_TEMPLATE_VERSION';

export interface IRcsValidationErrors {
  header?: string;
  description?: string;
  atLeastOneAction?: string;
  buttonLabelByActionId?: Record<string, string>;
  actionUrlByActionId?: Record<string, string>;
}

export interface IRcsActionConfig {
  id: string;
  actionType: string;
  buttonLabel: string;
  actionUrl: string;
}

export interface IRcsConfig {
  imageUrl: string;
  cardTitle: string;
  description: string;
  actions: IRcsActionConfig[];
  /** When false, SMS fallback fields are not persisted to template JSON and the UI is collapsed. */
  smsFallbackEnabled: boolean;
  smsFallbackTemplateId?: string;
  smsFallbackTemplateName?: string;
  smsFallbackContent?: string;
}

export interface IAction {
  type: ActionTypes;
  payload?: any;
}

export type Dispatch = (action: IAction) => void;

export interface IEditorSettings {
  theme: string;
  readOnly?: boolean;
}

export interface ISupportedLanguage {
  language: string;
}

export interface IRcsPreviewCompilationContext {
  customer?: { firstName?: string };
  phone?: { mobile?: string };
}

export interface IState {
  activeTab: RcsTemplateEditorTabs;
  rightWindowPercentage: number;
  hasUnsavedChanges: boolean;
  editorSettings: IEditorSettings;
  templateName: string;
  templateType: string;
  selectedLanguage: string;
  supportedLanguages: ISupportedLanguage[];
  rcsConfig: IRcsConfig;
  lastSavedRcsConfig: IRcsConfig;
  validationErrors: IRcsValidationErrors;
  compilationContext: IRcsPreviewCompilationContext;
  lastCompiledRcsPayload: string;
  lastCompiledCompilationContext: RcsCompilationContextForPreview;
  compiledTemplate: string;
  requiresPreview: boolean;
  snapshotJson: string;
  enrichmentConfigIds: string[];
  skipRandomiseSnapshotModal: boolean;
  previewingVersion: string;
}

function getStoredTheme(): string {
  try {
    if (typeof localStorage === 'undefined') {
      return DEFAULT_THEME;
    }
    const stored = localStorage.getItem(RCS_TEMPLATE_EDITOR_THEME_STORAGE_ID);
    return stored && ALLOWED_THEME_IDS.has(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export { defaultRcsConfig, defaultRcsPreviewCompilationContext } from '../defaults';

export const defaultContextValues: IState = {
  activeTab: RcsTemplateEditorTabs.templateForm,
  rightWindowPercentage: RCS_EDITOR_DEFAULT_PERCENTAGE,
  hasUnsavedChanges: false,
  editorSettings: {
    theme: getStoredTheme(),
    readOnly: false,
  },
  templateName: '',
  templateType: 'cartAbandonment',
  selectedLanguage: 'en',
  // Fallback when provider has not yet received template data; RcsTemplateEditorPage overwrites with API data.
  supportedLanguages: [{ language: 'en' }, { language: 'fr' }, { language: 'de' }],
  rcsConfig: defaultRcsConfig,
  lastSavedRcsConfig: defaultRcsConfig,
  validationErrors: {},
  compilationContext: defaultRcsPreviewCompilationContext,
  lastCompiledRcsPayload: '',
  lastCompiledCompilationContext: defaultRcsPreviewCompilationContext as RcsCompilationContextForPreview,
  compiledTemplate: '',
  requiresPreview: true,
  snapshotJson: DEFAULT_SNAPSHOT_JSON,
  enrichmentConfigIds: [],
  skipRandomiseSnapshotModal: false,
  previewingVersion: '',
};

export const RcsTemplateEditorStateContext = React.createContext<IState | undefined>(undefined);
export const RcsTemplateEditorDispatchContext = React.createContext<Dispatch | undefined>(undefined);

function useRcsTemplateEditorState() {
  const context = React.useContext(RcsTemplateEditorStateContext);
  if (context === undefined) {
    throw new Error('useRcsTemplateEditorState must be used within a RcsTemplateEditorProvider');
  }
  return context;
}

function useRcsTemplateEditorDispatch() {
  const context = React.useContext(RcsTemplateEditorDispatchContext);
  if (context === undefined) {
    throw new Error('useRcsTemplateEditorDispatch must be used within a RcsTemplateEditorProvider');
  }
  return context;
}

export { useRcsTemplateEditorState, useRcsTemplateEditorDispatch };
